/**
 * Safe URI encoding/decoding utilities that handle the special case of percent signs.
 *
 * The issue: React Router's history library has problems with percent signs in URLs.
 * When a folder name contains a % (like "100%"), it gets double-encoded/decoded incorrectly.
 *
 * Solution: Replace % with a placeholder before encoding, then reverse after decoding.
 */

const PERCENT_PLACEHOLDER = "<<PERCENT_SIGN>>";

/**
 * Safely encodes a URI component by replacing % signs with a placeholder first.
 * This prevents double-encoding issues with percent signs.
 */
export function safeEncodeURIComponent(str: string): string {
  // First, replace all % with placeholder
  const withPlaceholder = str.replace(/%/g, PERCENT_PLACEHOLDER);

  // Then encode normally
  return encodeURIComponent(withPlaceholder);
}

/**
 * Safely decodes a URI component by restoring % signs from placeholders after decoding.
 * This prevents double-decoding issues with percent signs.
 */
export function safeDecodeURIComponent(str: string): string {
  try {
    // First decode normally
    const decoded = decodeURIComponent(str);

    // Then replace placeholder back to %
    return decoded.replace(new RegExp(PERCENT_PLACEHOLDER, "g"), "%");
  } catch (e) {
    // If decoding fails (e.g., string wasn't encoded), return as-is
    return str;
  }
}

/**
 * Creates a folder URL with safe encoding.
 */
export function createSafeFolderUrl(folderName: string): string {
  return `/folders/${safeEncodeURIComponent(folderName)}`;
}

/**
 * Creates a folder review URL with safe encoding.
 */
export function createSafeFolderReviewUrl(folderName: string): string {
  return `/folders/${safeEncodeURIComponent(folderName)}/review`;
}

/**
 * Extracts folder name from pathname with safe decoding.
 * Handles both encoded and decoded pathnames gracefully.
 */
export function extractSafeFolderFromPathname(pathname: string): string | null {
  const folderPrefix = "/folders/";
  if (!pathname.startsWith(folderPrefix)) {
    return null;
  }

  const remaining = pathname.slice(folderPrefix.length);

  // If there's a sub-path (like /review), remove it
  const slashIndex = remaining.indexOf("/");
  const folderPart = slashIndex >= 0 ? remaining.slice(0, slashIndex) : remaining;

  // Try to decode, but if it fails, return as-is
  return safeDecodeURIComponent(folderPart);
}
